import React, { useEffect, useRef, useState } from 'react';
import { platesApi } from './api';
import { usePlatesDefaultAttachments } from './hooks';
import { ContextHeader, fmtDate } from './designHelpers';
import ModalShell from '../insurance/ModalShell';
import isotype from '../../../../../../Assets/peicar-isotype.svg';

// Etiqueta humana del origen de un adjunto default.
const SOURCE_LABELS = {
  insurance: 'Seguros',
  insurances: 'Seguros',
  seguros: 'Seguros',
  purchase_order: 'Orden de Compra',
  purchase_orders: 'Orden de Compra',
  oc: 'Orden de Compra',
  orden_compra: 'Orden de Compra',
};

function sourceLabel(src) {
  if (!src) return 'Otros';
  const key = String(src).toLowerCase();
  return SOURCE_LABELS[key] || (src === 'Seguros' || src === 'Orden de Compra' ? src : 'Otros');
}

export default function ComposeEmailModal({ visible, onHide, qpId, persona, view, toast, onSent }) {
  // El gestor es fijo (viene del template.to / template.gestor_name). No hay catálogo.
  const [gestorName, setGestorName] = useState('');
  const [toAddress, setToAddress] = useState('');
  const [cc, setCc] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [showCcInput, setShowCcInput] = useState(false);
  const [asunto, setAsunto] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  // FEATURE NUEVA — adjuntos default seleccionables (ids marcados).
  const [selectedDefaults, setSelectedDefaults] = useState({});
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [sending, setSending] = useState(false);

  const fileInputRef = useRef(null);
  const bodyRef = useRef(null);

  // Adjuntos default que ofrece el backend (Seguros / OC / Otros).
  const { attachments: defaultAttachments, loading: loadingDefaults } =
    usePlatesDefaultAttachments(qpId, visible);

  const dest = view?.gestor?.nombre || gestorName || '—';

  // Reset al cerrar
  useEffect(() => {
    if (!visible) return;
    setAttachments([]);
    setSelectedDefaults({});
  }, [visible]);

  // Todos los adjuntos default marcados por defecto cuando llegan.
  useEffect(() => {
    if (!visible) return;
    if (!Array.isArray(defaultAttachments) || !defaultAttachments.length) return;
    setSelectedDefaults((prev) => {
      const next = { ...prev };
      defaultAttachments.forEach((a) => {
        const k = defaultKey(a);
        if (!(k in next)) next[k] = true;
      });
      return next;
    });
  }, [visible, defaultAttachments]);

  // Sincroniza el body en el contentEditable cuando cambia desde fuera
  // (carga de plantilla), sin pisar el cursor mientras el admin escribe.
  useEffect(() => {
    const el = bodyRef.current;
    if (el && document.activeElement !== el && el.innerText !== body) {
      el.innerText = body;
    }
  }, [body]);

  // Cargar plantilla
  useEffect(() => {
    if (!visible || !qpId) return;
    setLoadingTemplate(true);
    platesApi.template(qpId, { step_type: 'register_plates' })
      .then((res) => {
        if (res?.data?.success) {
          const t = res.data.template || {};
          setAsunto(t.subject || '');
          setBody(t.body || '');
          // Pinta el cuerpo en el contentEditable de forma imperativa y diferida.
          // El effect [body] puede no alcanzar a correr tras el montaje del div
          // (los re-renders de usePlatesDefaultAttachments alteran el timing del
          // flush en React 17), así que lo forzamos cuando el div ya está montado.
          requestAnimationFrame(() => {
            const el = bodyRef.current;
            if (el && document.activeElement !== el) {
              el.innerText = t.body || '';
            }
          });
          if (t.to) setToAddress(t.to);
          if (t.gestor_name) setGestorName(t.gestor_name);
          if (Array.isArray(t.default_cc) && t.default_cc.length) {
            setCc((prev) => (prev.length ? prev : t.default_cc));
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoadingTemplate(false));
  }, [visible, qpId]);

  const addCc = () => {
    const v = ccInput.trim();
    if (v && /\S+@\S+/.test(v) && !cc.includes(v)) { setCc([...cc, v]); setCcInput(''); }
  };

  const handleFiles = (e) => {
    const list = Array.from(e.target.files || []).map((file) => ({
      id: 'f-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
      file,
      tipo: classifyTipo(file.name),
      nombre: file.name,
      size: formatBytes(file.size),
    }));
    setAttachments((prev) => [...prev, ...list]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const toggleDefault = (a) => {
    const k = defaultKey(a);
    setSelectedDefaults((prev) => ({ ...prev, [k]: !prev[k] }));
  };

  const selectedReferences = (defaultAttachments || []).filter((a) => selectedDefaults[defaultKey(a)]);
  const totalAdjuntos = attachments.length + selectedReferences.length;

  const canSend = !!toAddress && !!asunto && !!body && !sending && /\S+@\S+/.test(toAddress);

  const handleSend = async () => {
    if (!canSend) return;
    // Lee el body más reciente del contentEditable por si no hubo blur antes del click
    const finalBody = bodyRef.current ? bodyRef.current.innerText : body;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('to', toAddress);
      fd.append('gestor_name', gestorName || '');
      cc.forEach((c) => fd.append('cc[]', c));
      fd.append('subject', asunto);
      fd.append('body', finalBody);
      // Adjuntos NUEVOS subidos por el admin
      attachments.forEach((a, i) => {
        fd.append(`attachments[${i}][file]`, a.file);
        fd.append(`attachments[${i}][label]`, a.tipo);
      });
      // Adjuntos default conservados → references[i][source_type]/[source_id]
      selectedReferences.forEach((r, i) => {
        fd.append(`references[${i}][source_type]`, r.source_type);
        fd.append(`references[${i}][source_id]`, r.source_id);
      });

      const res = await platesApi.sendEmail(qpId, fd);
      if (res?.data?.success) {
        toast?.current?.show?.({ severity: 'success', summary: 'Solicitud enviada', detail: `A ${toAddress}`, life: 3500 });
        onSent?.();
        onHide?.();
      }
    } catch (e) {
      // 409 = Seguros no completado → muestra el mensaje del backend
      toast?.current?.show?.({ severity: 'error', summary: 'No se pudo enviar', detail: e?.response?.data?.message || e.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalShell visible={visible} onClose={sending ? () => {} : onHide} stacked width={1020} height={660}>
      <button className="modal-close" onClick={onHide} disabled={sending}><i className="pi pi-times" /></button>

      {sending && (
        <div style={{
          position: 'absolute', inset: 0,
          background: 'rgba(255,255,255,0.86)',
          backdropFilter: 'blur(2px)',
          zIndex: 20,
          display: 'grid', placeItems: 'center',
        }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner-cyan" style={{ width: 32, height: 32, borderWidth: 3, margin: '0 auto 16px' }} />
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--pei-text)' }}>Enviando solicitud…</div>
            <div style={{ fontSize: 13, color: 'var(--pei-text-helper)', marginTop: 4 }}>A {toAddress}</div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '440px 1fr', height: '100%', minHeight: 0 }}>
        {/* LEFT: form */}
        <div style={{ display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--pei-gray-5)', minHeight: 0 }}>
          <div style={{ padding: '26px 28px 14px' }}>
            <div className="eyebrow" style={{ marginBottom: 6 }}>Paso 6 · Placas</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--pei-text)', letterSpacing: '-0.008em' }}>
              Solicitar placas
            </div>
            <div style={{ fontSize: 13, color: 'var(--pei-text-helper)', marginTop: 4 }}>
              A {dest}
            </div>
          </div>

          <div className="scrollable" style={{ padding: '0 28px 20px', flex: 1, minHeight: 0 }}>
            <div style={{ background: 'var(--pei-bg-soft)', borderRadius: 14, padding: 14, marginBottom: 18 }}>
              <ContextHeader cliente={view?.cliente} vehiculo={view?.vehiculo} contrato={view?.contrato} compact />
              {/* view.contrato ya viene como "No. {numeroContrato}" desde buildView */}
            </div>

            {/* Gestor destino (fijo, editable como texto) */}
            <div style={{ marginBottom: 14 }}>
              <div className="pei-label">Gestor de placas destino</div>
              <div style={{
                border: '1.5px solid var(--pei-cyan-bright)',
                borderRadius: 12,
                padding: '8px 10px 8px 12px',
                display: 'flex', alignItems: 'center', gap: 10,
                background: 'white',
              }}>
                <div style={{
                  width: 26, height: 26, borderRadius: 6,
                  background: 'var(--pei-blue-deeper)', color: 'white',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <i className="pi pi-id-card" style={{ fontSize: 13 }} />
                </div>
                <input
                  className="pei-input"
                  value={gestorName}
                  onChange={(e) => setGestorName(e.target.value)}
                  placeholder="Nombre del gestor"
                  style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 14, fontWeight: 600, color: 'var(--pei-text)', padding: 0, height: 'auto' }}
                />
              </div>
            </div>

            {/* To */}
            <div style={{ marginBottom: 14 }}>
              <div className="pei-label">Para</div>
              <input
                className="pei-input"
                value={toAddress}
                onChange={(e) => setToAddress(e.target.value)}
                placeholder="correo@gestor.com"
                style={{ fontFamily: 'var(--pei-font-mono)', fontSize: 13 }}
              />
            </div>

            {/* CC */}
            <div style={{ marginBottom: 18 }}>
              <div className="pei-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span>CC</span>
                {!showCcInput && (
                  <button type="button" onClick={() => setShowCcInput(true)}
                    style={{ background: 'none', border: 'none', color: 'var(--pei-cyan)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'none', letterSpacing: 0 }}>
                    + Agregar
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {cc.map((email, i) => (
                  <span key={i} className="chip">
                    {email}
                    <span className="x" onClick={() => setCc(cc.filter((_, idx) => idx !== i))}>×</span>
                  </span>
                ))}
                {showCcInput && (
                  <input autoFocus className="pei-input"
                    placeholder="correo@ejemplo.com + Enter"
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); addCc(); }
                      if (e.key === 'Escape' && !ccInput) setShowCcInput(false);
                    }}
                    onBlur={() => { addCc(); if (!ccInput) setShowCcInput(false); }}
                    style={{ height: 34, fontSize: 13, flex: 1, minWidth: 160 }}
                  />
                )}
                {!showCcInput && cc.length === 0 && (
                  <span style={{ fontSize: 12, color: 'var(--pei-text-helper)', fontStyle: 'italic' }}>Sin destinatarios en copia</span>
                )}
              </div>
            </div>

            {/* Asunto */}
            <div style={{ marginBottom: 14 }}>
              <div className="pei-label">Asunto</div>
              <input className="pei-input" value={asunto} onChange={(e) => setAsunto(e.target.value)} disabled={loadingTemplate} />
            </div>

            {/* Hint: el mensaje se edita en la preview */}
            <div style={{
              background: 'rgba(50,184,223,0.07)',
              border: '1px dashed var(--pei-cyan-bright)',
              borderRadius: 12,
              padding: '12px 14px',
              marginBottom: 18,
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <i className="pi pi-pencil" style={{ color: 'var(--pei-cyan)', fontSize: 14 }} />
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--pei-text)', lineHeight: 1.3 }}>Edita el mensaje en la vista previa →</div>
                <div style={{ fontSize: 11, color: 'var(--pei-text-helper)', marginTop: 2 }}>Click sobre el cuerpo del correo a la derecha. Plantilla aplicada.</div>
              </div>
            </div>

            {/* FEATURE NUEVA — Adjuntos default (Seguros / OC / Otros) */}
            {(loadingDefaults || (defaultAttachments && defaultAttachments.length > 0)) && (
              <div style={{ marginBottom: 18 }}>
                <div className="pei-label" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                  <i className="pi pi-folder-open" style={{ fontSize: 11, color: 'var(--pei-cyan)' }} />
                  Documentos sugeridos
                  <span style={{ color: 'var(--pei-text-helper)', fontWeight: 500, letterSpacing: 0, textTransform: 'none', marginLeft: 4, fontSize: 11 }}>
                    · {selectedReferences.length} de {defaultAttachments?.length || 0} seleccionados
                  </span>
                </div>

                {loadingDefaults && (
                  <div style={{ fontSize: 12, color: 'var(--pei-text-helper)', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div className="spinner-cyan" style={{ width: 14, height: 14, borderWidth: 2 }} />
                    Cargando documentos…
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(defaultAttachments || []).map((a) => {
                    const k = defaultKey(a);
                    const checked = !!selectedDefaults[k];
                    return (
                      <DefaultAttachmentRow
                        key={k}
                        a={a}
                        checked={checked}
                        onToggle={() => toggleDefault(a)}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Attachments nuevos */}
            <div className="pei-label" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <i className="pi pi-paperclip" style={{ fontSize: 11, color: 'var(--pei-cyan)' }} />
              Adjuntos
              <span style={{ color: 'var(--pei-text-helper)', fontWeight: 500, letterSpacing: 0, textTransform: 'none', marginLeft: 4, fontSize: 11 }}>
                · {attachments.length} {attachments.length === 1 ? 'archivo' : 'archivos'}
              </span>
            </div>
            <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFiles} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {attachments.map(f => (
                <AttachmentChip key={f.id} f={f} onRemove={() => setAttachments(attachments.filter(a => a.id !== f.id))} />
              ))}
              <button className="chip-add" onClick={() => fileInputRef.current?.click()} type="button">
                <i className="pi pi-paperclip" style={{ fontSize: 11 }} /> Adjuntar
              </button>
            </div>
          </div>

          <div style={{ padding: '14px 28px 22px', borderTop: '1px solid var(--pei-gray-5)', display: 'flex', gap: 10 }}>
            <button className="pei-btn pei-btn-ghost" onClick={onHide} disabled={sending} style={{ flex: '0 0 auto' }}>Cancelar</button>
            <button className="pei-btn pei-btn-primary floor-shadow" onClick={handleSend} disabled={!canSend} style={{ flex: 1 }}>
              Enviar correo <i className="pi pi-send" style={{ fontSize: 12 }} />
            </button>
          </div>
        </div>

        {/* RIGHT: preview */}
        <div style={{ background: 'var(--pei-bg-readonly)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ padding: '18px 28px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--pei-text-label)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Vista previa · lo que va a recibir {dest}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ fontSize: 11, color: 'var(--pei-cyan)', display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
                <i className="pi pi-pencil" style={{ fontSize: 11 }} /> Editable
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--pei-text-helper)' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--pei-green-bright)' }} /> En vivo
              </div>
            </div>
          </div>

          <div className="scrollable" style={{ padding: '0 28px 24px', flex: 1, minHeight: 0 }}>
            <div style={{ background: 'white', borderRadius: 14, border: '1px solid var(--pei-gray-5)', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
              <div style={{ background: 'var(--pei-gradient-tri)', height: 6 }} />

              <div style={{ padding: '22px 28px 18px', borderBottom: '1px solid var(--pei-gray-5)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <img src={isotype} alt="" style={{ height: 24 }} />
                  <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.16em', color: 'var(--pei-blue-deeper)' }}>PEICAR</span>
                  <span style={{ fontSize: 11, color: 'var(--pei-text-helper)', marginLeft: 'auto' }}>{fmtDate(new Date().toISOString())}</span>
                </div>

                <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--pei-text)', lineHeight: 1.3, marginBottom: 10 }}>
                  {asunto || <span style={{ color: 'var(--pei-text-helper)' }}>Asunto…</span>}
                </div>

                <div style={{ fontSize: 12, color: 'var(--pei-text-helper)', lineHeight: 1.6 }}>
                  <strong style={{ color: 'var(--pei-text-label)', fontWeight: 600 }}>De:</strong> contratos@peicar.com.mx <br />
                  <strong style={{ color: 'var(--pei-text-label)', fontWeight: 600 }}>Para:</strong> {toAddress || '—'}
                  {cc.length > 0 && <><br /><strong style={{ color: 'var(--pei-text-label)', fontWeight: 600 }}>CC:</strong> {cc.join(', ')}</>}
                </div>
              </div>

              <div
                ref={bodyRef}
                contentEditable={!loadingTemplate && !sending}
                suppressContentEditableWarning
                onBlur={(e) => setBody(e.currentTarget.innerText)}
                className="editable-body"
                data-placeholder="Escribe el mensaje…"
                style={{ padding: '20px 28px 26px', fontSize: 14.5, color: 'var(--pei-text)', lineHeight: 1.65, whiteSpace: 'pre-wrap', outline: 'none', cursor: 'text', minHeight: 220 }}
              />

              {totalAdjuntos > 0 && (
                <div style={{ padding: '0 28px 22px' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--pei-text-label)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i className="pi pi-paperclip" style={{ fontSize: 11 }} />
                    {totalAdjuntos} archivo{totalAdjuntos === 1 ? '' : 's'} adjunto{totalAdjuntos === 1 ? '' : 's'}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {selectedReferences.map((r) => (
                      <div key={defaultKey(r)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px 8px 10px', border: '1px solid var(--pei-gray-5)', borderRadius: 10, background: 'white', minWidth: 200 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--pei-bg-soft)', display: 'grid', placeItems: 'center', color: 'var(--pei-cyan)', flexShrink: 0 }}>
                          <i className="pi pi-file-pdf" style={{ fontSize: 13, color: 'var(--pei-danger)' }} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--pei-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>{r.file_name}</div>
                          <div style={{ fontSize: 11, color: 'var(--pei-text-helper)' }}>{r.label || sourceLabel(r.source)}</div>
                        </div>
                      </div>
                    ))}
                    {attachments.map(f => (
                      <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px 8px 10px', border: '1px solid var(--pei-gray-5)', borderRadius: 10, background: 'white', minWidth: 200 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--pei-bg-soft)', display: 'grid', placeItems: 'center', color: 'var(--pei-cyan)', flexShrink: 0 }}>
                          <i className="pi pi-file-pdf" style={{ fontSize: 13, color: 'var(--pei-danger)' }} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--pei-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>{f.nombre}</div>
                          <div style={{ fontSize: 11, color: 'var(--pei-text-helper)' }}>{f.tipo} · {f.size}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ padding: '16px 28px', background: 'var(--pei-bg-soft)', borderTop: '1px solid var(--pei-gray-5)', fontSize: 11, color: 'var(--pei-text-helper)', textAlign: 'center', letterSpacing: '0.04em' }}>
                fácil · rápido · digital <span style={{ margin: '0 10px' }}>·</span> peicar.com.mx
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .editable-body:hover { background: rgba(50,184,223,0.06); }
        .editable-body:focus { background: rgba(50,184,223,0.10); box-shadow: inset 0 0 0 2px rgba(52,198,241,0.35); }
        .editable-body:empty::before {
          content: attr(data-placeholder);
          color: var(--pei-text-helper);
        }
      `}</style>
    </ModalShell>
  );
}

function defaultKey(a) {
  return `${a.source_type}:${a.source_id}`;
}

function classifyTipo(name = '') {
  const lc = name.toLowerCase();
  if (lc.includes('cotiz')) return 'Cotización';
  if (lc.includes('tarjeta') || lc.includes('circul')) return 'Tarjeta';
  if (lc.includes('placa')) return 'Placa';
  if (lc.includes('ine'))     return 'INE';
  if (lc.includes('factura')) return 'Factura';
  if (lc.includes('carta'))   return 'Carta';
  if (lc.includes('cfe') || lc.includes('domicilio')) return 'Comp. domicilio';
  return 'Adjunto';
}

function formatBytes(b) {
  if (!b && b !== 0) return '';
  const k = 1024;
  if (b < k) return b + ' B';
  if (b < k * k) return Math.round(b / k) + ' KB';
  return (b / (k * k)).toFixed(1) + ' MB';
}

// Etiqueta de color del origen del adjunto default.
function sourceChipStyle(label) {
  const map = {
    'Seguros':          { bg: 'rgba(34,236,154,0.20)', color: '#0a7c4c' },
    'Orden de Compra':  { bg: 'rgba(50,184,223,0.14)', color: 'var(--pei-cyan)' },
    'Otros':            { bg: 'rgba(15,23,42,0.06)',   color: 'var(--pei-text-label)' },
  };
  return map[label] || map['Otros'];
}

function DefaultAttachmentRow({ a, checked, onToggle }) {
  const origin = sourceLabel(a.source);
  const c = sourceChipStyle(origin);
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 12px 8px 10px',
        border: `1px solid ${checked ? 'var(--pei-cyan-bright)' : 'var(--pei-gray-5)'}`,
        borderRadius: 10,
        background: checked ? 'rgba(50,184,223,0.06)' : 'white',
        cursor: 'pointer', textAlign: 'left', width: '100%',
        fontFamily: 'inherit', transition: 'border-color .12s, background .12s',
      }}
    >
      <span style={{
        width: 18, height: 18, borderRadius: 5, flexShrink: 0,
        border: `1.5px solid ${checked ? 'var(--pei-cyan)' : 'var(--pei-gray-4, #cbd5e1)'}`,
        background: checked ? 'var(--pei-cyan)' : 'white',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: 'white',
      }}>
        {checked && <i className="pi pi-check" style={{ fontSize: 10, fontWeight: 700 }} />}
      </span>
      <div style={{ width: 30, height: 30, borderRadius: 7, background: 'var(--pei-bg-soft)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
        <i className="pi pi-file-pdf" style={{ fontSize: 12, color: 'var(--pei-danger)' }} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--pei-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {a.file_name}
        </div>
        {a.label && (
          <div style={{ fontSize: 11, color: 'var(--pei-text-helper)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {a.label}
          </div>
        )}
      </div>
      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 999, background: c.bg, color: c.color, flexShrink: 0 }}>
        {origin}
      </span>
    </button>
  );
}

function AttachmentChip({ f, onRemove }) {
  const map = {
    'Cotización':      { bg: 'rgba(50,184,223,0.14)', color: 'var(--pei-cyan)' },
    'Tarjeta':         { bg: 'rgba(34,236,154,0.20)', color: '#0a7c4c' },
    'Placa':           { bg: 'rgba(34,236,154,0.20)', color: '#0a7c4c' },
    'INE':             { bg: 'rgba(255,108,14,0.14)', color: 'var(--pei-orange)' },
    'Carta':           { bg: 'rgba(32,75,123,0.10)',  color: 'var(--pei-blue-deeper)' },
    'Comp. domicilio': { bg: 'rgba(50,184,223,0.14)', color: 'var(--pei-cyan)' },
    'Factura':         { bg: 'rgba(34,236,154,0.20)', color: '#0a7c4c' },
  };
  const c = map[f.tipo] || { bg: 'rgba(15,23,42,0.06)', color: 'var(--pei-text-label)' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 8px 6px 6px', background: 'white', border: '1px solid var(--pei-gray-5)', borderRadius: 999, fontSize: 12 }}>
      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 999, background: c.bg, color: c.color }}>{f.tipo}</span>
      <span style={{ color: 'var(--pei-text)', fontWeight: 500 }}>{f.nombre}</span>
      <span style={{ color: 'var(--pei-text-helper)', fontSize: 11 }}>{f.size}</span>
      <span onClick={onRemove} style={{ cursor: 'pointer', color: 'var(--pei-text-helper)', padding: '0 4px', lineHeight: 1, fontSize: 14 }}>×</span>
    </span>
  );
}
